import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";

export default function TeamInformation() {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [resources, setResources] = useState([]);
  const [editingTeam, setEditingTeam] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [newResource, setNewResource] = useState({ resource_name: "", resource_notes: "" });
  
  const [activeTab, setActiveTab] = useState("team"); // "team" | "invite" | "resources"

  // Fetch team info
  useEffect(() => {
    const fetchTeam = async () => {
      const { data, error } = await supabase
        .from("teams")
        .select("*")
        .eq("id", teamId)
        .single();
      if (error) console.error(error);
      else setTeam(data);
    };
    fetchTeam();

    const fetchResources = async () => {
      const { data, error } = await supabase
        .from("team_resources")
        .select("*")
        .eq("team_id", teamId);
      if (error) console.error(error);
      else setResources(data);
    };
    fetchResources();
  }, [teamId]);

  if (!team) return <p>Loading...</p>;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">{team.name} Dashboard</h1>

      {/* Tabs */}
      <div className="flex border-b mb-6">
        {["team", "invite", "resources"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium -mb-px border-b-2 transition ${
              activeTab === tab
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            {tab === "team" ? "Team Info" : tab === "invite" ? "Invite User" : "Resources"}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === "team" && (
          <div className="bg-white p-6 rounded shadow">
            <h2 className="text-xl font-semibold mb-4">Team Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {["name", "sport", "location_address", "team_identity", "offensive_scheme", "defensive_scheme"].map((field) => (
                <div key={field}>
                  <label className="block font-medium">{field.replace("_", " ").toUpperCase()}</label>
                  <input
                    type="text"
                    name={field}
                    value={team[field] || ""}
                    onChange={(e) => setTeam({ ...team, [field]: e.target.value })}
                    disabled={!editingTeam}
                    className="border rounded w-full p-2"
                  />
                </div>
              ))}
            </div>
            <div className="mt-4">
              {editingTeam ? (
                <button
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                  onClick={() => {
                    supabase
                      .from("teams")
                      .update({
                        name: team.name,
                        sport: team.sport,
                        location_address: team.location_address,
                        team_identity: team.team_identity,
                        offensive_scheme: team.offensive_scheme,
                        defensive_scheme: team.defensive_scheme,
                      })
                      .eq("id", team.id)
                      .then(() => setEditingTeam(false));
                  }}
                >
                  Save
                </button>
              ) : (
                <button
                  className="bg-green-500 text-white px-4 py-2 rounded"
                  onClick={() => setEditingTeam(true)}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === "invite" && (
          <div className="bg-white p-6 rounded shadow">
            <h2 className="text-xl font-semibold mb-4">Invite a Coach</h2>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Enter user email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="border rounded w-full p-2"
              />
              <button
                onClick={() => {
                  console.log("Invited user:", inviteEmail);
                  setInviteEmail("");
                }}
                className="bg-purple-500 text-white px-4 py-2 rounded"
              >
                Invite
              </button>
            </div>
          </div>
        )}

        {activeTab === "resources" && (
          <div className="space-y-4">
            {resources.map((res, index) => (
              <div key={res.id} className="bg-gray-50 p-4 rounded shadow">
                <input
                  type="text"
                  value={res.resource_name}
                  onChange={(e) => {
                    const updated = [...resources];
                    updated[index].resource_name = e.target.value;
                    setResources(updated);
                  }}
                  className="border p-2 w-full mb-2"
                  placeholder="Resource Name"
                />
                <textarea
                  value={res.resource_notes}
                  onChange={(e) => {
                    const updated = [...resources];
                    updated[index].resource_notes = e.target.value;
                    setResources(updated);
                  }}
                  className="border p-2 w-full mb-2"
                  placeholder="Resource Notes"
                />
                <button
                  onClick={() => {
                    if (res.id) {
                      supabase
                        .from("team_resources")
                        .update({
                          resource_name: res.resource_name,
                          resource_notes: res.resource_notes,
                        })
                        .eq("id", res.id)
                        .then(() => console.log("Resource updated"));
                    }
                  }}
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  Save
                </button>
              </div>
            ))}

            {/* Add new resource */}
            <div className="bg-white p-4 rounded shadow mt-4">
              <input
                type="text"
                placeholder="Resource Name"
                value={newResource.resource_name}
                onChange={(e) => setNewResource({ ...newResource, resource_name: e.target.value })}
                className="border p-2 w-full mb-2"
              />
              <textarea
                placeholder="Resource Notes"
                value={newResource.resource_notes}
                onChange={(e) => setNewResource({ ...newResource, resource_notes: e.target.value })}
                className="border p-2 w-full mb-2"
              />
              <button
                onClick={() => {
                  supabase
                    .from("team_resources")
                    .insert({ ...newResource, team_id: teamId })
                    .then(() => {
                      setResources([...resources, newResource]);
                      setNewResource({ resource_name: "", resource_notes: "" });
                    });
                }}
                className="bg-green-500 text-white px-4 py-2 rounded"
              >
                Add Resource
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
